import React, { useState, useEffect } from 'react';
import { productAPI, listingAPI, groupAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ProfilePhoto } from '../ProfilePhoto';
import { Coins, Plus, Pencil, Trash2, ShoppingBag, Tag, Package, MessageCircle, Store, ShoppingCart, CheckCircle2, Download, Link2, ExternalLink, History, ClipboardList, Clock, XCircle, UsersRound, Crown, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { formatCoins, getStatusLabel, getStatusBadgeClass, formatDate } from '../../lib/utils';
import { RichTextEditor, RichTextDisplay, stripHtml } from '../common/RichTextEditor';

export default function MarketplaceTab({ classId, isProfessor, wallet, onRefresh }) {
  const [products, setProducts] = useState([]);
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('products');
  
  // Purchase history (professor and student)
  const [allPurchases, setAllPurchases] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  const [purchasesSummary, setPurchasesSummary] = useState(null);
  
  // Product dialog state
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price_coins: 10,
    stock: null,
    image_url: '',
    is_active: true,
    requires_approval: false,
    has_download_link: false,
    download_url: '',
    download_label: ''
  });

  // Listing dialog state
  const [listingDialogOpen, setListingDialogOpen] = useState(false);
  const [listingForm, setListingForm] = useState({
    title: '',
    description: '',
    price_coins: 10,
    category: '',
    image_url: '',
    contact_method: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [purchasingId, setPurchasingId] = useState(null);
  
  // Group purchase state (for group leaders)
  const [leaderGroups, setLeaderGroups] = useState([]);
  const [groupWallets, setGroupWallets] = useState({});
  const [groupPurchaseDialogOpen, setGroupPurchaseDialogOpen] = useState(false);
  const [selectedProductForGroup, setSelectedProductForGroup] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  
  // Filter for purchase history
  const [purchaseFilter, setPurchaseFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, [classId]);

  const loadData = async () => {
    try {
      const [productsRes, listingsRes] = await Promise.all([
        productAPI.list(classId),
        listingAPI.list(classId)
      ]);
      setProducts(productsRes.data);
      setListings(listingsRes.data.filter(l => l.status === 'APPROVED'));
      
      if (isProfessor) {
        // Load purchases and summary for professor
        const [purchasesRes, summaryRes] = await Promise.all([
          productAPI.getAllPurchases(classId),
          productAPI.getPurchasesSummary(classId)
        ]);
        setAllPurchases(purchasesRes.data);
        setPurchasesSummary(summaryRes.data);
      } else {
        // Load student's listings, purchases, and groups
        const [myListingsRes, myPurchasesRes, myGroupsRes] = await Promise.all([
          listingAPI.myListings(classId),
          productAPI.getMyPurchases(classId),
          groupAPI.getMyGroups(classId)
        ]);
        setMyListings(myListingsRes.data);
        setMyPurchases(myPurchasesRes.data);
        
        // Filter groups where student is leader
        const groups = myGroupsRes.data || [];
        const leaderGrps = groups.filter(g => g.is_leader);
        setLeaderGroups(leaderGrps);
        
        // Load wallets for leader groups
        if (leaderGrps.length > 0) {
          const wallets = {};
          for (const g of leaderGrps) {
            try {
              const wRes = await groupAPI.getGroupWallet(classId, g.id);
              wallets[g.id] = wRes.data;
            } catch { wallets[g.id] = { balance: 0 }; }
          }
          setGroupWallets(wallets);
        }
      }
    } catch (error) {
      toast.error('Error al cargar marketplace');
    } finally {
      setLoading(false);
    }
  };

  // Product functions
  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      price_coins: 10,
      stock: null,
      image_url: '',
      is_active: true,
      requires_approval: false,
      has_download_link: false,
      download_url: '',
      download_label: ''
    });
    setEditingProduct(null);
  };

  const handleOpenEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price_coins: product.price_coins,
      stock: product.stock,
      image_url: product.image_url || '',
      is_active: product.is_active,
      requires_approval: product.requires_approval || false,
      has_download_link: product.has_download_link || false,
      download_url: product.download_url || '',
      download_label: product.download_label || ''
    });
    setProductDialogOpen(true);
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        ...productForm,
        stock: productForm.stock === '' ? null : productForm.stock
      };
      if (editingProduct) {
        await productAPI.update(classId, editingProduct.id, data);
        toast.success('Producto actualizado');
      } else {
        await productAPI.create(classId, data);
        toast.success('Producto creado');
      }
      setProductDialogOpen(false);
      resetProductForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await productAPI.delete(classId, productId);
      toast.success('Producto eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handlePurchaseProduct = async (product) => {
    if (wallet?.balance < product.price_coins) {
      toast.error('Balance insuficiente');
      return;
    }
    const confirmMsg = product.requires_approval 
      ? `¿Solicitar ${product.name} por ${product.price_coins} coins? El profesor deberá aprobar tu compra.`
      : `¿Comprar ${product.name} por ${product.price_coins} coins?`;
    if (!window.confirm(confirmMsg)) return;
    
    setPurchasingId(product.id);
    try {
      const res = await productAPI.purchase(classId, product.id);
      if (res.data.status === 'pending') {
        toast.success('¡Solicitud enviada! Esperando aprobación del profesor.');
      } else {
        toast.success(`¡Compraste ${product.name}!`);
      }
      loadData();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al comprar');
    } finally {
      setPurchasingId(null);
    }
  };

  // Group purchase flow
  const handleOpenGroupPurchase = (product) => {
    if (leaderGroups.length === 1) {
      setSelectedProductForGroup(product);
      setSelectedGroupId(leaderGroups[0].id);
      setGroupPurchaseDialogOpen(true);
    } else {
      setSelectedProductForGroup(product);
      setSelectedGroupId(null);
      setGroupPurchaseDialogOpen(true);
    }
  };

  const handleConfirmGroupPurchase = async () => {
    if (!selectedProductForGroup || !selectedGroupId) return;
    
    const groupBalance = groupWallets[selectedGroupId]?.balance || 0;
    if (groupBalance < selectedProductForGroup.price_coins) {
      toast.error('Balance grupal insuficiente');
      return;
    }

    setPurchasingId(`group-${selectedProductForGroup.id}`);
    setGroupPurchaseDialogOpen(false);
    try {
      const res = await groupAPI.groupPurchase(classId, selectedGroupId, selectedProductForGroup.id);
      toast.success(res.data.message || '¡Compra grupal exitosa!');
      loadData();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en compra grupal');
    } finally {
      setPurchasingId(null);
      setSelectedProductForGroup(null);
      setSelectedGroupId(null);
    }
  };
  
  const handleApprovePurchase = async (purchaseId) => {
    try {
      await productAPI.approvePurchase(classId, purchaseId);
      toast.success('Compra aprobada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    }
  };
  
  const handleRejectPurchase = async (purchaseId) => {
    if (!window.confirm('¿Rechazar esta compra?')) return;
    try {
      await productAPI.rejectPurchase(classId, purchaseId);
      toast.success('Compra rechazada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    }
  };

  // Listing functions
  const resetListingForm = () => {
    setListingForm({
      title: '',
      description: '',
      price_coins: 10,
      category: '',
      image_url: '',
      contact_method: ''
    });
  };

  const handleSubmitListing = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await listingAPI.create(classId, listingForm);
      toast.success('Anuncio enviado para aprobación');
      setListingDialogOpen(false);
      resetListingForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear anuncio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('¿Eliminar este anuncio?')) return;
    try {
      await listingAPI.delete(classId, listingId);
      toast.success('Anuncio eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handlePurchaseListing = async (listing) => {
    if (wallet?.balance < listing.price_coins) {
      toast.error('Balance insuficiente');
      return;
    }
    if (!window.confirm(`¿Comprar "${listing.title}" por ${listing.price_coins} coins? El pago irá directamente al vendedor.`)) return;
    
    setPurchasingId(listing.id);
    try {
      await listingAPI.purchase(classId, listing.id);
      toast.success(`¡Compra exitosa! Los coins han sido transferidos a ${listing.seller_name}`);
      loadData();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al comprar');
    } finally {
      setPurchasingId(null);
    }
  };
  
  // Filter purchases by status
  const filteredPurchases = purchaseFilter === 'all' 
    ? allPurchases 
    : allPurchases.filter(p => p.status === purchaseFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <TabsList>
            <TabsTrigger value="products" data-testid="subtab-products">
              <Store className="w-4 h-4 mr-2" />
              Tienda
            </TabsTrigger>
            {isProfessor && (
              <TabsTrigger value="purchases" data-testid="subtab-purchases">
                <ClipboardList className="w-4 h-4 mr-2" />
                Compras
              </TabsTrigger>
            )}
            <TabsTrigger value="bulletin" data-testid="subtab-bulletin">
              <Tag className="w-4 h-4 mr-2" />
              Entre Estudiantes
            </TabsTrigger>
            {!isProfessor && (
              <>
                <TabsTrigger value="my-purchases" data-testid="subtab-my-purchases">
                  <History className="w-4 h-4 mr-2" />
                  Mis Compras
                </TabsTrigger>
                <TabsTrigger value="my-listings" data-testid="subtab-my-listings">
                  <Package className="w-4 h-4 mr-2" />
                  Mis Anuncios
                </TabsTrigger>
              </>
            )}
          </TabsList>

          <div className="flex gap-2">
            {isProfessor && activeSubTab === 'products' && (
              <Dialog open={productDialogOpen} onOpenChange={(open) => { setProductDialogOpen(open); if (!open) resetProductForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-product-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Producto
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[85vh] overflow-y-auto">
                  <form onSubmit={handleSubmitProduct}>
                    <DialogHeader>
                      <DialogTitle className="font-heading">
                        {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
                      </DialogTitle>
                      <DialogDescription>
                        Crea un producto que los estudiantes pueden comprar con coins
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Nombre</Label>
                        <Input
                          placeholder="Ej: Punto extra en examen"
                          value={productForm.name}
                          onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                          required
                          data-testid="product-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Descripción</Label>
                        <RichTextEditor
                          value={productForm.description}
                          onChange={(val) => setProductForm({...productForm, description: val})}
                          placeholder="Describe el producto o recompensa"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Precio (Coins)</Label>
                          <div className="relative">
                            <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                            <Input
                              type="number"
                              min="1"
                              value={productForm.price_coins}
                              onChange={(e) => setProductForm({...productForm, price_coins: parseInt(e.target.value) || 0})}
                              className="pl-9"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Stock (opcional)</Label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="Ilimitado"
                            value={productForm.stock ?? ''}
                            onChange={(e) => setProductForm({...productForm, stock: e.target.value === '' ? null : parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>URL de Imagen (opcional)</Label>
                        <Input
                          placeholder="https://..."
                          value={productForm.image_url}
                          onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                        />
                      </div>
                      
                      {/* Download Link Section */}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-blue-700 flex items-center gap-2">
                              <Link2 className="w-4 h-4" />
                              Enlace de Descarga
                            </Label>
                            <p className="text-xs text-blue-600">Añade un enlace que el estudiante verá al comprar</p>
                          </div>
                          <Switch
                            checked={productForm.has_download_link}
                            onCheckedChange={(checked) => setProductForm({...productForm, has_download_link: checked})}
                          />
                        </div>
                        
                        {productForm.has_download_link && (
                          <>
                            <div className="space-y-2">
                              <Label>URL del Enlace</Label>
                              <Input
                                placeholder="https://drive.google.com/... o https://calendly.com/..."
                                value={productForm.download_url}
                                onChange={(e) => setProductForm({...productForm, download_url: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Texto del Botón</Label>
                              <Input
                                placeholder="Ej: Descargar Guía, Agendar Sesión"
                                value={productForm.download_label}
                                onChange={(e) => setProductForm({...productForm, download_label: e.target.value})}
                              />
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Requiere Aprobación</Label>
                          <p className="text-xs text-slate-500">El profesor debe aprobar cada compra</p>
                        </div>
                        <Switch
                          checked={productForm.requires_approval}
                          onCheckedChange={(checked) => setProductForm({...productForm, requires_approval: checked})}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Label>Producto Activo</Label>
                        <Switch
                          checked={productForm.is_active}
                          onCheckedChange={(checked) => setProductForm({...productForm, is_active: checked})}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={submitting}>
                        {submitting ? 'Guardando...' : (editingProduct ? 'Actualizar' : 'Crear')}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {!isProfessor && activeSubTab === 'bulletin' && (
              <Dialog open={listingDialogOpen} onOpenChange={(open) => { setListingDialogOpen(open); if (!open) resetListingForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-listing-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Vender Algo
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <form onSubmit={handleSubmitListing}>
                    <DialogHeader>
                      <DialogTitle className="font-heading">Vender Producto</DialogTitle>
                      <DialogDescription>
                        Tu anuncio será revisado antes de publicarse. Los compradores pagarán directamente con coins.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Título</Label>
                        <Input
                          placeholder="Ej: Libro de Cálculo II"
                          value={listingForm.title}
                          onChange={(e) => setListingForm({...listingForm, title: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Descripción</Label>
                        <RichTextEditor
                          value={listingForm.description}
                          onChange={(val) => setListingForm({...listingForm, description: val})}
                          placeholder="Describe lo que ofreces"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Precio (Coins)</Label>
                          <div className="relative">
                            <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                            <Input
                              type="number"
                              min="1"
                              value={listingForm.price_coins}
                              onChange={(e) => setListingForm({...listingForm, price_coins: parseInt(e.target.value) || 0})}
                              className="pl-9"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Categoría</Label>
                          <Input
                            placeholder="Ej: Libros"
                            value={listingForm.category}
                            onChange={(e) => setListingForm({...listingForm, category: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>URL de Imagen (opcional)</Label>
                        <Input
                          placeholder="https://..."
                          value={listingForm.image_url}
                          onChange={(e) => setListingForm({...listingForm, image_url: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Método de Contacto</Label>
                        <Input
                          placeholder="Ej: WhatsApp, Email, etc."
                          value={listingForm.contact_method}
                          onChange={(e) => setListingForm({...listingForm, contact_method: e.target.value})}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={submitting}>
                        {submitting ? 'Enviando...' : 'Enviar para Aprobación'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Products Tab */}
        <TabsContent value="products">
          {products.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ShoppingBag className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">No hay productos en la tienda</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.filter(p => p.is_active || isProfessor).map((product) => (
                <Card 
                  key={product.id} 
                  className={`border-slate-200 overflow-hidden ${!product.is_active ? 'opacity-60' : ''}`}
                  data-testid={`product-card-${product.id}`}
                >
                  {product.image_url && (
                    <div className="aspect-video bg-slate-100 overflow-hidden">
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="font-heading text-lg">{product.name}</CardTitle>
                      <div className="flex gap-1 flex-shrink-0">
                        {!product.is_active && <Badge variant="secondary">Inactivo</Badge>}
                        {product.has_download_link && (
                          <Badge className="bg-blue-100 text-blue-700">
                            <Link2 className="w-3 h-3 mr-1" />
                            Enlace
                          </Badge>
                        )}
                        {product.requires_approval && (
                          <Badge className="bg-amber-100 text-amber-700">Aprobación</Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-slate-600 mb-4 line-clamp-2"><RichTextDisplay content={product.description} /></div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full font-bold">
                        <Coins className="w-4 h-4" />
                        {formatCoins(product.price_coins)}
                      </div>
                      {product.stock !== null && (
                        <span className="text-sm text-slate-500">
                          Stock: {product.stock}
                        </span>
                      )}
                    </div>
                    
                    {/* Show download link for students who have purchased */}
                    {!isProfessor && product.download_url && (
                      <a 
                        href={product.download_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full mb-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {product.download_label || 'Acceder'}
                      </a>
                    )}

                    {isProfessor ? (
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => handleOpenEditProduct(product)}
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Editar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Button 
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          disabled={purchasingId === product.id || (wallet?.balance || 0) < product.price_coins || (product.stock !== null && product.stock <= 0)}
                          onClick={() => handlePurchaseProduct(product)}
                          data-testid={`buy-product-${product.id}`}
                        >
                          {purchasingId === product.id ? (
                            'Procesando...'
                          ) : (wallet?.balance || 0) < product.price_coins ? (
                            'Balance insuficiente'
                          ) : product.stock !== null && product.stock <= 0 ? (
                            'Agotado'
                          ) : (
                            <>
                              <ShoppingBag className="w-4 h-4 mr-2" />
                              {product.requires_approval ? 'Solicitar' : 'Comprar'}
                            </>
                          )}
                        </Button>
                        {leaderGroups.length > 0 && (product.stock === null || product.stock > 0) && (
                          <Button
                            variant="outline"
                            className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                            disabled={purchasingId === `group-${product.id}`}
                            onClick={() => handleOpenGroupPurchase(product)}
                            data-testid={`buy-group-${product.id}`}
                          >
                            {purchasingId === `group-${product.id}` ? (
                              'Procesando...'
                            ) : (
                              <>
                                <UsersRound className="w-4 h-4 mr-2" />
                                Comprar con Grupo
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Professor: Purchase History Tab */}
        {isProfessor && (
          <TabsContent value="purchases">
            <div className="space-y-6">
              {/* Summary Cards */}
              {purchasesSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="border-slate-200">
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Total Productos</p>
                      <p className="text-2xl font-bold">{purchasesSummary.total_products}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Total Ventas</p>
                      <p className="text-2xl font-bold text-emerald-600">{purchasesSummary.total_sales}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Coins Vendidos</p>
                      <p className="text-2xl font-bold text-amber-600">{formatCoins(purchasesSummary.total_revenue)}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="pt-4">
                      <p className="text-sm text-slate-500">Pendientes</p>
                      <p className="text-2xl font-bold text-blue-600">{purchasesSummary.pending_approvals}</p>
                    </CardContent>
                  </Card>
                </div>
              )}
              
              {/* Product Inventory */}
              {purchasesSummary?.products && purchasesSummary.products.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Inventario de Productos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {purchasesSummary.products.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="font-medium">{p.name}</span>
                            {!p.is_active && <Badge variant="secondary">Inactivo</Badge>}
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-slate-500">Stock: {p.stock ?? '∞'}</span>
                            <span className="text-emerald-600">Vendidos: {p.sold_count}</span>
                            {p.pending_count > 0 && (
                              <Badge className="bg-amber-100 text-amber-700">{p.pending_count} pendientes</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Purchase Filter */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500">Filtrar:</span>
                {['all', 'pending', 'completed', 'rejected'].map(status => (
                  <button
                    key={status}
                    onClick={() => setPurchaseFilter(status)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      purchaseFilter === status 
                        ? 'bg-slate-900 text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {status === 'all' ? 'Todas' : status === 'pending' ? 'Pendientes' : status === 'completed' ? 'Completadas' : 'Rechazadas'}
                  </button>
                ))}
              </div>
              
              {/* Purchase List */}
              {filteredPurchases.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <ClipboardList className="w-12 h-12 text-slate-300 mb-4" />
                    <p className="text-slate-500">No hay compras {purchaseFilter !== 'all' ? 'con este filtro' : ''}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredPurchases.map(purchase => (
                    <Card key={purchase.id} className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <ProfilePhoto photoUrl={purchase.buyer_photo_url} name={purchase.buyer_name} size={40} />
                            <div>
                              <p className="font-medium">{purchase.buyer_name}</p>
                              <p className="text-sm text-slate-500">
                                Compró: <span className="font-medium">{purchase.product_name}</span>
                              </p>
                              <p className="text-xs text-slate-400">{formatDate(purchase.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1 text-amber-600 font-bold">
                              <Coins className="w-4 h-4" />
                              {formatCoins(purchase.price_paid)}
                            </div>
                            {purchase.status === 'pending' ? (
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => handleApprovePurchase(purchase.id)}>
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Aprobar
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRejectPurchase(purchase.id)}>
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <Badge className={
                                purchase.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                              }>
                                {purchase.status === 'completed' ? 'Completada' : 'Rechazada'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        )}

        {/* Student Marketplace Tab */}
        <TabsContent value="bulletin">
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-blue-700 text-sm">
              <ShoppingCart className="w-4 h-4 inline mr-2" />
              Marketplace entre estudiantes: compra directamente con tus coins. El pago va al vendedor.
            </p>
          </div>
          
          {listings.length === 0 ? (
            <Card className="border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Tag className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">No hay productos de estudiantes</p>
                {!isProfessor && (
                  <p className="text-sm text-slate-400">¡Sé el primero en vender algo!</p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {listings.map((listing) => (
                <Card 
                  key={listing.id} 
                  className="border-slate-200 overflow-hidden"
                  data-testid={`listing-card-${listing.id}`}
                >
                  {listing.image_url && (
                    <div className="aspect-video bg-slate-100 overflow-hidden">
                      <img 
                        src={listing.image_url} 
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <CardTitle className="font-heading text-lg">{listing.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <ProfilePhoto photoUrl={listing.seller_photo_url} name={listing.seller_name} size={24} />
                          <span className="text-sm text-slate-500">{listing.seller_name}</span>
                        </div>
                      </div>
                      {listing.category && (
                        <Badge variant="secondary">{listing.category}</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-slate-600 mb-4 line-clamp-2"><RichTextDisplay content={listing.description} /></div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full font-bold">
                        <Coins className="w-4 h-4" />
                        {formatCoins(listing.price_coins)}
                      </div>
                    </div>

                    {listing.contact_method && (
                      <p className="text-sm text-slate-500 mb-4">
                        <MessageCircle className="w-4 h-4 inline mr-1" />
                        {listing.contact_method}
                      </p>
                    )}

                    {!isProfessor && (
                      <Button 
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        disabled={purchasingId === listing.id || (wallet?.balance || 0) < listing.price_coins}
                        onClick={() => handlePurchaseListing(listing)}
                        data-testid={`buy-listing-${listing.id}`}
                      >
                        {purchasingId === listing.id ? (
                          'Procesando...'
                        ) : (wallet?.balance || 0) < listing.price_coins ? (
                          'Balance insuficiente'
                        ) : (
                          <>
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Comprar Ahora
                          </>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Student: My Purchases Tab */}
        {!isProfessor && (
          <TabsContent value="my-purchases">
            {myPurchases.length === 0 ? (
              <Card className="border-dashed border-2 border-slate-200">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <History className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No has realizado compras</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {myPurchases.map(purchase => (
                  <Card key={purchase.id} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {purchase.product_image_url ? (
                            <img src={purchase.product_image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                              <ShoppingBag className="w-6 h-6 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{purchase.product_name}</p>
                            <p className="text-xs text-slate-400">{formatDate(purchase.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1 text-amber-600 font-bold">
                            <Coins className="w-4 h-4" />
                            {formatCoins(purchase.price_paid)}
                          </div>
                          <Badge className={
                            purchase.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 
                            purchase.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }>
                            {purchase.status === 'completed' ? (
                              <><CheckCircle2 className="w-3 h-3 mr-1" />Completada</>
                            ) : purchase.status === 'pending' ? (
                              <><Clock className="w-3 h-3 mr-1" />Pendiente</>
                            ) : (
                              <><XCircle className="w-3 h-3 mr-1" />Rechazada</>
                            )}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Show download link if available */}
                      {purchase.status === 'completed' && purchase.download_url && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <a 
                            href={purchase.download_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                          >
                            <ExternalLink className="w-4 h-4" />
                            {purchase.download_label || 'Acceder'}
                          </a>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* My Listings Tab (Student) */}
        {!isProfessor && (
          <TabsContent value="my-listings">
            {myListings.length === 0 ? (
              <Card className="border-dashed border-2 border-slate-200">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Package className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">No has publicado anuncios</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {myListings.map((listing) => (
                  <Card 
                    key={listing.id} 
                    className={`border-slate-200 ${listing.status === 'SOLD' ? 'opacity-60' : ''}`}
                    data-testid={`my-listing-${listing.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="font-heading text-lg">{listing.title}</CardTitle>
                        <span className={`${getStatusBadgeClass(listing.status)} flex items-center gap-1`}>
                          {listing.status === 'SOLD' && <CheckCircle2 className="w-3 h-3" />}
                          {getStatusLabel(listing.status)}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-slate-600 mb-4 overflow-hidden"><RichTextDisplay content={listing.description} /></div>
                      
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full font-bold w-fit mb-4">
                        <Coins className="w-4 h-4" />
                        {formatCoins(listing.price_coins)}
                      </div>

                      {listing.status === 'PENDING' && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteListing(listing.id)}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Eliminar
                        </Button>
                      )}
                      
                      {listing.status === 'SOLD' && (
                        <p className="text-sm text-emerald-600 font-medium">
                          <CheckCircle2 className="w-4 h-4 inline mr-1" />
                          ¡Vendido! Recibiste {formatCoins(listing.price_coins)} coins
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Group Purchase Dialog */}
      <Dialog open={groupPurchaseDialogOpen} onOpenChange={setGroupPurchaseDialogOpen}>
        <DialogContent data-testid="group-purchase-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersRound className="w-5 h-5 text-blue-600" />
              Compra Grupal
            </DialogTitle>
            <DialogDescription>
              Compra "{selectedProductForGroup?.name}" usando el wallet de tu grupo
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Product info */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="font-medium">{selectedProductForGroup?.name}</span>
              <div className="flex items-center gap-1 text-amber-600 font-bold">
                <Coins className="w-4 h-4" />
                {formatCoins(selectedProductForGroup?.price_coins || 0)}
              </div>
            </div>

            {/* Group selection */}
            {leaderGroups.length > 1 ? (
              <div className="space-y-2">
                <Label>Selecciona el grupo</Label>
                <div className="space-y-2">
                  {leaderGroups.map(g => {
                    const balance = groupWallets[g.id]?.balance || 0;
                    const hasEnough = balance >= (selectedProductForGroup?.price_coins || 0);
                    return (
                      <button
                        key={g.id}
                        onClick={() => setSelectedGroupId(g.id)}
                        disabled={!hasEnough}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                          selectedGroupId === g.id
                            ? 'border-blue-500 bg-blue-50'
                            : hasEnough
                              ? 'border-slate-200 hover:border-blue-300'
                              : 'border-slate-100 opacity-50 cursor-not-allowed'
                        }`}
                        data-testid={`select-group-${g.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${g.color}20` }}>
                            <UsersRound className="w-4 h-4" style={{ color: g.color }} />
                          </div>
                          <span className="font-medium text-sm">{g.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Wallet className="w-3.5 h-3.5 text-amber-500" />
                          <span className={`text-sm font-semibold ${hasEnough ? 'text-amber-600' : 'text-red-500'}`}>
                            {formatCoins(balance)}
                          </span>
                          {!hasEnough && <span className="text-xs text-red-400 ml-1">Insuficiente</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : leaderGroups.length === 1 ? (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${leaderGroups[0].color}20` }}>
                    <UsersRound className="w-4 h-4" style={{ color: leaderGroups[0].color }} />
                  </div>
                  <div>
                    <span className="font-medium text-sm">{leaderGroups[0].name}</span>
                    <div className="flex items-center gap-1">
                      <Crown className="w-3 h-3 text-amber-500" />
                      <span className="text-xs text-slate-500">Eres el lider</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Wallet className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-sm font-semibold text-amber-600">
                    {formatCoins(groupWallets[leaderGroups[0].id]?.balance || 0)}
                  </span>
                </div>
              </div>
            ) : null}

            {/* Balance warning */}
            {selectedGroupId && (groupWallets[selectedGroupId]?.balance || 0) < (selectedProductForGroup?.price_coins || 0) && (
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded">
                El grupo no tiene suficiente balance para esta compra.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupPurchaseDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmGroupPurchase}
              disabled={!selectedGroupId || (groupWallets[selectedGroupId]?.balance || 0) < (selectedProductForGroup?.price_coins || 0)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-group-purchase-btn"
            >
              <UsersRound className="w-4 h-4 mr-2" />
              Confirmar Compra Grupal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
